<template>
  <nav id="divTopBar" :class="topBarClass">
    <div v-show="visiblePreviewPreset" class="w-100 h-100">
      <div class="preset--preview">
        <div class="content">
          <div class="icon-wrapper">
            <i class="icon icon-preview" />
          </div>
          <div class="text">
            You are previewing how the <b>{{ currentPreset && currentPreset.name }}</b> will restyle your page. You can
            not edit Elements in Preset Preview Mode.
          </div>
        </div>
        <div class="action">
          <div class="screen-dropdown">
            <GfDropdown
              :options="screenOptions"
              :value="defaultScreenDrop"
              f-type="icon"
              :visible-name="false"
              dropdown-class="gf_global-style__preview-option_dropdown"
              dropdown-item-class="gf_global-style__preview-option_dropdown-item"
              @selected="changeScreen">
            </GfDropdown>
          </div>
          <div class="button-common">
            <button class="btn btn-quit-preview" @click="quitPreview">
              <i class="icon icon-close"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-show="!visiblePreviewPreset" class="w-100">
      <div id="pageInfo" class="d-flex align-items-center">
        <a
          :href="backToDashboardUrl"
          class="logo d-flex align-items-center justify-content-center"
          :class="{ 'has-version-label': isDisplayVersionLabel }"
          @click="handleBackToDashboard">
          <svg width="150" height="50" viewBox="0 0 150 170" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              class="wing-yellow"
              d="M144.958 9.05009L5.75326 52.6795C5.19173 52.8323 4.88655 53.4495 5.03914 54.0668L15.7754 89.4335C15.928 89.9958 16.5444 90.3564 17.106 90.1547L119.25 58.1248C124.108 56.5847 128.247 53.199 130.596 48.5787C136.938 36.362 142.199 23.6319 146.24 10.4435C146.545 9.56345 145.776 8.79341 144.958 9.05009Z"
              fill="currentColor" />
            <path
              class="wing-magenta"
              d="M111.572 70.2256L25.0712 97.3236C24.6134 97.4764 24.3021 97.9897 24.4547 98.5031L33.5552 128.376C33.7078 128.84 34.2205 129.146 34.6783 128.993L89.8427 111.698C93.9809 110.414 97.4112 107.487 99.4559 103.637C104.821 93.3206 109.27 82.5401 112.646 71.4051C112.902 70.6839 112.237 70.0178 111.572 70.2256Z"
              fill="currentColor" />
            <path
              class="wing-blue"
              d="M78.6791 124.855L42.6312 136.167C42.2223 136.271 42.0148 136.73 42.1185 137.145L49.7846 162.398C49.8884 162.807 50.3462 163.064 50.7551 162.911L60.3256 159.91C63.8046 158.835 66.7161 156.372 68.4556 153.084C73.0028 144.357 76.7382 135.275 79.6008 125.827C79.851 125.216 79.2895 124.647 78.6791 124.855Z"
              fill="currentColor" />
          </svg>
          <div v-if="isDisplayVersionLabel" class="version-label">v6</div>
        </a>
        <ul class="breadcrumb typo-p12 typo-regular w-full">
          <a class="breadcrumb-item home" :href="bindingData.returnUrl" @click="handleBackToPageList">{{
            bindingData.returnText
          }}</a>
          <p
            v-tooltip.bottom="{ content: pageTitle, class: !shouldShowTooltip ? 'd-none' : 'd-block' }"
            class="edit-ttl-btn !flex w-full max-w-full items-center"
            @click="toggleEditPageTitleModal">
            <span
              v-if="productPageDefault"
              class="mr-[8px] flex h-[16px] max-h-4 w-[50px] min-w-[50px] items-center rounded-[3px] bg-blue-100 px-[4px] text-xs font-medium leading-[14px] text-primary-350">
              Default
            </span>
            <span ref="pageTtlRef" class="edit-ttl">{{ pageTitle }}</span>
            <i class="icon-t-blog edit-icon" />
          </p>
        </ul>
      </div>
      <div class="pageTool d-flex grow-1 shrink-1 align-items-center flex-1" :class="pageToolClass">
        <div v-if="isRunningAbTest" class="ab-test__blocking"></div>
        <div class="w-100">
          <GemXSwitchVariant />
          <a
            id="gf-new-library"
            class="gf_action-item btn btn-secondary btn-default"
            title="New Library"
            @click="openNewLibrary">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M0.699951 2.19922C0.699951 1.37079 1.37152 0.699219 2.19995 0.699219H5.79995C6.62838 0.699219 7.29995 1.37079 7.29995 2.19922V5.79922C7.29995 6.62765 6.62838 7.29922 5.79995 7.29922H2.19995C1.37152 7.29922 0.699951 6.62765 0.699951 5.79922V2.19922ZM2.19995 1.69922C1.92381 1.69922 1.69995 1.92308 1.69995 2.19922V5.79922C1.69995 6.07536 1.92381 6.29922 2.19995 6.29922H5.79995C6.07609 6.29922 6.29995 6.07536 6.29995 5.79922V2.19922C6.29995 1.92308 6.07609 1.69922 5.79995 1.69922H2.19995Z"
                fill="currentColor" />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M8.69995 2.19922C8.69995 1.37079 9.37152 0.699219 10.2 0.699219H13.8C14.6284 0.699219 15.3 1.37079 15.3 2.19922V5.79922C15.3 6.62765 14.6284 7.29922 13.8 7.29922H10.2C9.37152 7.29922 8.69995 6.62765 8.69995 5.79922V2.19922ZM10.2 1.69922C9.92381 1.69922 9.69995 1.92308 9.69995 2.19922V5.79922C9.69995 6.07536 9.92381 6.29922 10.2 6.29922H13.8C14.0761 6.29922 14.3 6.07536 14.3 5.79922V2.19922C14.3 1.92308 14.0761 1.69922 13.8 1.69922H10.2Z"
                fill="currentColor" />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M8.69995 10.1992C8.69995 9.37079 9.37152 8.69922 10.2 8.69922H13.8C14.6284 8.69922 15.3 9.37079 15.3 10.1992V13.7992C15.3 14.6276 14.6284 15.2992 13.8 15.2992H10.2C9.37152 15.2992 8.69995 14.6276 8.69995 13.7992V10.1992ZM10.2 9.69922C9.92381 9.69922 9.69995 9.92308 9.69995 10.1992V13.7992C9.69995 14.0754 9.92381 14.2992 10.2 14.2992H13.8C14.0761 14.2992 14.3 14.0754 14.3 13.7992V10.1992C14.3 9.92308 14.0761 9.69922 13.8 9.69922H10.2Z"
                fill="currentColor" />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M0.699951 10.1992C0.699951 9.37079 1.37152 8.69922 2.19995 8.69922H5.79995C6.62838 8.69922 7.29995 9.37079 7.29995 10.1992V13.7992C7.29995 14.6276 6.62838 15.2992 5.79995 15.2992H2.19995C1.37152 15.2992 0.699951 14.6276 0.699951 13.7992V10.1992ZM2.19995 9.69922C1.92381 9.69922 1.69995 9.92308 1.69995 10.1992V13.7992C1.69995 14.0754 1.92381 14.2992 2.19995 14.2992H5.79995C6.07609 14.2992 6.29995 14.0754 6.29995 13.7992V10.1992C6.29995 9.92308 6.07609 9.69922 5.79995 9.69922H2.19995Z"
                fill="currentColor" />
            </svg>
            <span class="typo-button-14 typo-medium text text-light ml-1">Library</span>
          </a>
          <AssignedProduct v-if="isDisplayAssignedProduct" @openAssignProductModal="openAssignProductModal" />
          <ResponsiveTools class="mx-auto" />
          <HistoryControl />
          <NavbarActions />
          <AssignProductModal v-if="isAssignProductModal" @close="closeAssignProductModal" />
          <ProductPickerPopup v-if="isOpenProductPickerPopup" @close="closeProductPickerPopup" />
        </div>
      </div>
    </div>
    <PopupOnboarding
      v-if="obGlobalStyle.applyPreset && !obGlobalStyle.publicPreset && linkedPresetId"
      class="notice-publish-preset"
      :p-style="publishPresetStyle"
      :style-arrow="publishPresetStyleArrow"
      :title="$t('navbar.publishFirstTimeGS.title')"
      type-o-b="publicPreset"
      :content="$t('navbar.publishFirstTimeGS.desc')" />
  </nav>
</template>

<script lang="ts">
import { actions, mutations } from '@modules/libraries/store/types';
import { actions as pageActions } from '@/store/page/types';
import { mutations as mutaSidebar } from '@modules/editor-sidebar/store/types';
import type { Component } from 'vue';
import {
  mutationTypes as productPickerMutationTypes,
  getterTypes as productPickerGetterTypes,
  MODULE_NAME as PODUCT_PICKER_MODULE,
} from '@/modules/product-picker';
import { defineComponent, defineAsyncComponent } from 'vue';
import { mapMutations, mapState, mapGetters, mapActions } from 'vuex';
import _store from '@/app/store/store';
import { $JQuery } from '@/plugins';
import { app } from '@/store';
import emitter from '@/emitter';
import NavbarActions from './NavbarActions.vue';
const ResponsiveTools = defineAsyncComponent(() => import('./ResponsiveTools.vue'));
const HistoryControl = defineAsyncComponent(() => import('./HistoryControl.vue'));
const AssignedProduct = defineAsyncComponent(() => import('./AssignedProduct.vue'));
const AssignProductModal = defineAsyncComponent(
  () => import('@/views/Navbar/AssignProductModal/AssignProductModal.vue'),
);
const ProductPickerPopup = defineAsyncComponent<Component>(() => import('@/modules/product-picker'));
const PopupOnboarding = defineAsyncComponent(() => import('@/modules/global-style'));
import { getEmbedAppUrl } from '@/utils/getEmbedAppUrl';
import { BUILDER_OPEN_NEW_TAB } from '@/constants/feature-flag-key';
import { checkKeyValid } from '@/app/utils/checkRoleApply';
import { GemXSwitchVariant } from '@/modules/ab-testing';
import { postMessageToParent } from '@/app/utils/helpers';

export default defineComponent({
  name: 'Navbar',
  components: {
    GemXSwitchVariant,
    ResponsiveTools,
    HistoryControl,
    NavbarActions,
    PopupOnboarding,
    AssignedProduct,
    AssignProductModal,
    ProductPickerPopup,
  },
  props: {
    v2: {
      type: Boolean,
      default: null,
    },
  },
  data: () => ({
    publishPresetStyle: {
      position: 'absolute',
      top: '60px',
      right: '56px',
    },
    publishPresetStyleArrow: {
      position: 'absolute',
      right: '24px',
      top: '-14px',
      transform: 'rotate(90deg)',
    },
    height: 0,
    defaultScreenDrop: 'lg',
    shouldShowTooltip: false,
    windowWidth: 0,
  }),

  computed: {
    ...mapState('app', {
      pageInfo: 'pageInfo',
      shopInfo: 'shopInfo',
      shopMeta: 'shopMeta',
      enablePassword: 'enablePassword',
      screenOptions: 'screenOptions',
    }),
    ...mapGetters('page', {
      defaultPageTitle: 'defaultPageTitle',
      loadingFrame: 'loadingFrame',
      isAssignProductModal: 'getIsAssignProductModal',
    }),
    ...mapGetters('sidebar', {
      presetIsEdited: 'PRESET_IS_EDITED',
    }),
    ...mapGetters(PODUCT_PICKER_MODULE, {
      isOpenProductPickerPopup: productPickerGetterTypes.GET_IS_OPEN_POPUP,
    }),
    ...mapState('sidebar', {
      previewPreset: 'previewPreset',
      isPublishPreset: 'isPublishPreset',
      currentPreset: 'currentPreset',
      linkedPresetId: 'linkedPresetId',
    }),
    obGlobalStyle() {
      return this.shopMeta.onboardingGlobalStyle;
    },
    visiblePreviewPreset() {
      return this.previewPreset && this.linkedPresetId !== this.currentPreset.id;
    },
    ...mapGetters('app', {
      isRunningAbTest: 'isRunningAbTest',
      productPageDefault: 'productPageDefault',
    }),
    embedAppUrl() {
      return getEmbedAppUrl(this.shopInfo?.shopifyDomain);
    },
    pageTitle() {
      _store.setValue('change-page-name', this.defaultPageTitle);
      let title = this.defaultPageTitle || '';
      if (!title && this.pageInfo) title = this.pageInfo.page.title;

      return title;
    },
    bindingData() {
      const pageType = this.pageInfo?.page?.type;
      let text;

      const isGemPagesV7 = this.shopInfo?.isGemPagesV7 === 'true';

      switch (pageType) {
        case 'template-index': {
          text = 'Home Pages';
          break;
        }
        case 'template-product': {
          text = 'Product Pages';
          break;
        }
        case 'template-collection': {
          text = 'Collection Pages';
          break;
        }
        case 'template-article': {
          text = 'Article Pages';
          break;
        }
        case 'template-page':
        default: {
          text = 'Landing Pages';
          break;
        }
      }
      const embedDashboardPageType: Record<string, string> = {
        'template-index': 'GP_INDEX',
        'template-product': 'GP_PRODUCT',
        'template-collection': 'GP_COLLECTION',
        'template-article': 'GP_ARTICLE',
        'template-page': 'GP_STATIC',
      };
      const searchParams = new URLSearchParams({
        page_type: embedDashboardPageType[pageType] ?? 'STATIC',
        version: isGemPagesV7 ? 'v7' : 'v6',
      });
      const url = `${this.embedAppUrl}/pages?${searchParams}`;

      return {
        returnUrl: url,
        returnText: text,
      };
    },
    backToDashboardUrl() {
      return this.embedAppUrl;
    },
    pageToolClass() {
      return this.isRunningAbTest ? 'ab-test__blur' : '';
    },
    topBarClass() {
      return this.isRunningAbTest ? 'overflow-hidden' : '';
    },
    isDisplayAssignedProduct() {
      return this.pageInfo?.page?.type == 'template-product';
    },
    maxWidthPageTitle() {
      if (this.productPageDefault) {
        return this.windowWidth <= 1300 ? 104 : 134;
      }
      return this.windowWidth <= 1300 ? 162 : 192;
    },
    isDisplayVersionLabel() {
      return ['mixed'].includes(this.shopInfo?.isGemPagesV7 || '');
    },
  },
  watch: {
    pageTitle() {
      this.$nextTick(() => {
        if (this.$refs.pageTtlRef && (this.$refs.pageTtlRef as HTMLElement).clientWidth >= this.maxWidthPageTitle) {
          this.shouldShowTooltip = true;
        } else {
          this.shouldShowTooltip = false;
        }
      });
    },
  },

  created() {
    this.$center.navbar = this;
    this.windowWidth = window.innerWidth;
  },

  async mounted() {
    this.height = (this.$el as HTMLElement)?.offsetHeight;
    this.$nextTick(() => {});
    this.$nextTick(() => {
      window.addEventListener('resize', () => {
        this.windowWidth = window.innerWidth;
      });
      if (this.$refs.pageTtlRef && (this.$refs.pageTtlRef as HTMLElement).clientWidth >= this.maxWidthPageTitle) {
        this.shouldShowTooltip = true;
      }
    });
  },
  methods: {
    ...mapMutations('library', {
      toggle: mutations.TOGGLE,
      changeLayout: mutations.CHANGE_LAYOUT,
      changeTab: mutations.CHANGE_TAB,
    }),
    ...mapMutations('sidebar', {
      setPreviewPreset: mutaSidebar.SET_PREVIEW_PRESET,
      setIsPublishPreset: mutaSidebar.SET_IS_PUBLISH_PRESET,
    }),
    ...mapActions('app', {
      updateShopMeta: 'updateShopMeta',
    }),
    ...mapActions('library', {
      getTemplates: actions.GET_TEMPLATE_DATA,
    }),
    ...mapActions('page', {
      getAssignedProduct: pageActions.GET_ASSIGNED_PRODUCT,
    }),
    ...mapMutations(PODUCT_PICKER_MODULE, {
      setOpenProductPickerPopup: productPickerMutationTypes.SET_IS_OPEN_POPUP,
    }),
    handleBackToPageList(e: Event) {
      e.preventDefault();

      const embedDashboardPageType: Record<string, string> = {
        'template-index': 'GP_INDEX',
        'template-product': 'GP_PRODUCT',
        'template-collection': 'GP_COLLECTION',
        'template-article': 'GP_ARTICLE',
        'template-page': 'GP_STATIC',
      };

      const pageType = embedDashboardPageType[this.pageInfo?.page?.type] ?? 'STATIC';
      const version = this.shopInfo?.isGemPagesV7 === 'true' ? 'v7' : 'v6';

      const searchParams = new URLSearchParams({
        page_type: pageType,
        version,
      });
      const url = `${this.embedAppUrl}/pages?${searchParams}`;
      if (checkKeyValid(BUILDER_OPEN_NEW_TAB)) {
        if (window.top) {
          window.top.location.href = url;
        } else {
          window.location.href = url;
        }
        return;
      }

      const data = {
        type: 'windowBackToPageList',
        pageType,
        version,
      };

      postMessageToParent(data);
    },
    handleBackToDashboard(e: Event) {
      e.preventDefault();
      if (checkKeyValid(BUILDER_OPEN_NEW_TAB)) {
        if (window.top) {
          window.top.location.href = this.embedAppUrl;
        } else {
          window.location.href = this.embedAppUrl;
        }
        return;
      }
      postMessageToParent({ type: 'windowCloseBuilder' });
    },
    handleWithoutPresetEdited() {
      emitter.emit('WithoutPresetEdited', this.bindingData.returnUrl);

      emitter.on('CallBackWithoutPresetEdited', (returnUrl: string): void => {
        if (!returnUrl) return;
        window.location.href = returnUrl;
      });
    },
    quitPreview() {
      this.setPreviewPreset(false);
      const $gfFrameContents = ($JQuery as any)('#gfFrame').contents();
      $gfFrameContents.find('.gryffeditor .gf_gs-global-style-preview').remove();
      $gfFrameContents.find('.gf-overlayPreviewPreset').remove();
      $gfFrameContents.find('.gryffeditor .gf_gs-global-style').attr('type', 'text/css');

      this.handleStyleQuitPreview($gfFrameContents);
    },
    handleStyleQuitPreview($gfFrameContents: any) {
      const allStyleKeys = (app as any).$store.state['editor']['beforeStylePreviewModeGlobalStyle'];
      for (const keyStyle in allStyleKeys) {
        const value = (app as any).$store.getters['editor/GET_BEFORE_STYLE_PREVIEW_MODE'](keyStyle);
        const idStyle =
          typeof keyStyle === 'string'
            ? keyStyle.replace('-row', '').replace('-element', '').replace('-module', '')
            : '';

        $gfFrameContents.find(`#${idStyle} .style`).html(value);
        (app as any).$store.commit('editor/SET_DATA_SETTINGS', {
          id: keyStyle,
          value: value,
        });
      }
    },
    changeScreen(size: any) {
      const $gfResTool = ($JQuery as any)('#divTopBar').find('#gfResTool').gfResTool();
      $gfResTool.data('gfrestool').resizeScreen(size.value);
    },
    openNewLibrary() {
      this.toggle(true);
      this.changeLayout('default');
    },
    toggleEditPageTitleModal() {
      this.$emit('toggleEditPageTitleModal');
    },
    openAssignProductModal() {
      (app as any).$store.commit('page/UPDATE_IS_ASSIGN_PRODUCT_MODEL', true);
    },
    closeAssignProductModal() {
      (app as any).$store.commit('page/UPDATE_IS_ASSIGN_PRODUCT_MODEL', false);
    },
    closeProductPickerPopup() {
      this.setOpenProductPickerPopup(false);
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
.w-100 {
  width: 100%;
  display: flex;
}

.h-100 {
  height: 100%;
}

#divTopBar {
  position: absolute;
  top: 0;
  right: 0;
  left: auto;
  z-index: 99999;

  height: $menu-size;
  width: 100%;
  background: $dark4;
  color: #ffffff;
  display: flex;
}

#pageInfo {
  width: $sidebar-size;
  svg {
    display: inline;
    vertical-align: unset;
  }

  .logo {
    width: $menu-size;
    height: $menu-size;
    color: $text-dark-high;

    &.has-version-label {
      padding-right: 12px;
      padding-left: 8px;
      width: fit-content !important;
      height: 40px !important;
      border-radius: $radius;
      background-color: $black2;
      margin-right: 16px;
      margin-left: 8px;
      @include trans-all;
    }

    .version-label {
      padding: 3px 4px;
      color: #f9f9f9;
      font-size: 14px;
      font-family: Inter;
      font-style: normal;
      font-weight: 500;
      line-height: 20px;
    }

    svg {
      width: 24px;
    }

    .wing-yellow,
    .wing-magenta,
    .wing-blue {
      @include trans-all;
    }

    &:hover {
      &.has-version-label {
        background-color: #fff3;
      }
      .wing-yellow {
        color: $rb-yellow;
      }

      .wing-magenta {
        color: $rb-red;
      }

      .wing-blue {
        color: $rb-blue;
      }
    }
  }

  .edit-ttl-btn {
    color: $text-dark-high;
    font-size: 14px;
    line-height: 16px;
    font-weight: 500;
    cursor: pointer;
    margin-left: -8px;
    padding: 4px 8px;
    transition: all 0.25s;
    border-radius: 3px;
    display: inline-block;
    width: fit-content;
    &:hover {
      background-color: rgba(#fff, 0.2);
    }
  }
  .edit-ttl {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 8px;
    display: inline-block;
    flex: 1;
    display: block;
    width: 100%;
  }
}

.pageTool {
  position: relative;
  svg {
    display: inline;
    vertical-align: unset;
  }
}

.breadcrumb {
  max-width: $sidebar-size - $menu-size;

  .breadcrumb-item {
    display: flex;
    white-space: nowrap;
    align-items: center;
    color: $text-dark-low;
    @include trans-all;

    a {
      color: inherit;
    }

    &:first-child::after {
      content: '\e907';
      font-family: eicon;
      font-weight: 400;
      font-style: normal;
      font-variant: normal;
      line-height: 1;
      text-rendering: auto;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      font-size: 17px;
      line-height: 12px;
      color: $text-dark-low;
    }

    &.active,
    &:hover {
      color: $text-dark-high;
    }

    &:not(:first-child) {
      margin-top: 4px;
    }
  }
}

#gf-new-library {
  padding: 11px 14px;
  @media (max-width: 1300px) {
    .text {
      display: none;
    }
  }
}

.page-name {
  max-width: 100%;
}

@media (max-width: 1300px) {
  #pageInfo {
    width: $sidebar-size-sm;
  }

  .breadcrumb {
    max-width: $sidebar-size-sm - $menu-size;
  }
}

.preset--preview {
  background-color: #fed571;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;

  .content {
    @include flex-center;

    .icon-wrapper {
      font-size: 16px;
      color: $black3;
      margin-right: 9px;
      margin-left: 19px;
      @include flex-center;
    }

    .text {
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 150%;
      color: #212121;
    }
  }

  .action {
    @include flex-center;

    .button-common {
      margin-left: 8px;
      margin-right: 16px;
      position: relative;

      .btn {
        background: #494949;
        border-radius: 3px;
        height: 40px;
        font-weight: 500;
        font-size: 14px;
        line-height: 16px;
        border: 1px solid #323232;
        color: #fff;

        &:hover {
          background: #323232;
        }
      }
    }
  }

  .gf_global-style__preview-option_dropdown {
    :deep(.gf_btn--dropdown) {
      border: unset;
      color: #212121;
      background: inherit;

      &:hover {
        background: #edc66a;
      }

      i {
        font-size: 20px;
      }
    }
  }

  .btn-quit-preview {
    width: 40px;

    i {
      font-size: 20px;
    }
  }
}
</style>
